/**
 * MobileSubstitutionCard
 *
 * Mobile-first card for a single absent teacher's substitution coverage.
 * Replaces the horizontal frozen-panel grid on small screens.
 *
 * Layout:
 *   ┌─────────────────────────────────────────┐
 *   │ Avatar  Teacher name   Status chip  ⚡  │
 *   │         Subject · Class             🗑  │
 *   ├─────────────────────────────────────────┤
 *   │ Progress bar (covered / total periods)  │
 *   ├─────────────────────────────────────────┤
 *   │ Period pill  Period pill  Period pill …  │
 *   └─────────────────────────────────────────┘
 *
 * Each period pill shows:
 *   - Period number + time
 *   - Class + subject
 *   - Substitute name (or "Unassigned")
 *   - Color-coded border: amber=unassigned, blue=draft, green=posted, red=conflict
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Avatar,
  LinearProgress,
  CircularProgress,
  Tooltip,
  Paper,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from '@mui/material';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import UndoIcon from '@mui/icons-material/Undo';
import CloseIcon from '@mui/icons-material/Close';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDraftAssignments,
  getTimetableRecordsDirect,
  triggerEngine,
  rerunAutoAssignment,
  discardDraft,
  recallAndRedraft,
  getByTeacherDirect,
  updateAssignment,
  createAssignment,
  removeAssignment,
  getTeachersDirect,
} from '@timetable/api';
import type {
  DraftAssignment,
  AbsenceWithStatus,
  SchedulePeriod,
  AutoAssignProgress,
  Teacher,
} from '@timetable/types';
import { useAuthStore } from '@/store/authStore';
import { useToast } from '@/components/Toast';
import { queryKeys } from '@/lib/queryKeys';
import { TeacherSelector } from '@/components/absences/TeacherSelector';
import { AutoAssignProgressDialog } from './AutoAssignProgressDialog';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Context for a period that already has a DB assignment record */
interface AssignModalState {
  assignment: DraftAssignment;
  conflictInfo?: { type: 'scheduling' | 'absent' | 'inactive'; label: string };
  cantAutoAssign?: boolean;
}

/** Context for a period with no DB record yet */
interface NewAssignContext {
  absenceId: string;
  absentTeacherId: string;
  periodId: string;
  classSectionId: string;
  subjectId: string;
  className: string;
  subjectName: string;
  periodNumber: number;
  timeStart: string;
  timeEnd: string;
}

export interface MobileSubstitutionCardProps {
  absence: AbsenceWithStatus;
  date: string;
  absentTeacherIds: Set<string>;
  isAdmin: boolean;
  /** Called when stats change so parent can aggregate */
  onStatsChange?: (absenceId: string, pending: number, covered: number, published: number) => void;
}

// ─── useBusyTeacherIds ────────────────────────────────────────────────────────

function useBusyTeacherIds(
  schoolId: string | null,
  dayOfWeek: string,
  timeStart: string,
  timeEnd: string,
): Set<string> {
  const { data: records = [] } = useQuery({
    queryKey: [...queryKeys.timetable.all, 'busy-teachers', schoolId, dayOfWeek],
    queryFn: () =>
      getTimetableRecordsDirect({
        school_id: schoolId!,
        day_of_week: dayOfWeek as any,
      }),
    enabled: !!schoolId && !!dayOfWeek && !!timeStart && !!timeEnd,
    staleTime: 30_000,
  });

  return useMemo(() => {
    const busy = new Set<string>();
    for (const r of records as any[]) {
      const rStart = r.period?.time_start;
      const rEnd = r.period?.time_end;
      if (!rStart || !rEnd) continue;
      if (rStart < timeEnd && rEnd > timeStart) busy.add(r.teacher_id);
    }
    return busy;
  }, [records, timeStart, timeEnd]);
}

// ─── SuggestionCard ───────────────────────────────────────────────────────────

interface SuggestionCardProps {
  teacher: Teacher;
  subjectMatch: boolean;
  isBusy: boolean;
  isSelected: boolean;
  onClick: () => void;
}

function SuggestionCard({ teacher, subjectMatch, isBusy, isSelected, onClick }: SuggestionCardProps) {
  const initials = teacher.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 1.25,
        cursor: 'pointer',
        borderColor: isSelected && !isBusy ? 'primary.main' : 'divider',
        bgcolor: isBusy ? 'grey.50' : isSelected ? 'primary.light' : 'background.paper',
        borderWidth: isSelected && !isBusy ? 2 : 1,
        opacity: isBusy ? 0.6 : 1,
        transition: 'all 0.15s',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 130,
        flexShrink: 0,
        borderRadius: 2,
        '&:active': { opacity: 0.7 },
      }}
    >
      <Avatar
        sx={{
          width: 28,
          height: 28,
          fontSize: 11,
          bgcolor: isBusy ? 'grey.400' : isSelected ? 'primary.main' : 'primary.light',
          color: isSelected ? 'primary.contrastText' : 'primary.dark',
        }}
      >
        {initials}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600 }} noWrap>
          {teacher.name}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
          {isBusy && (
            <Chip label="Busy" size="small" color="error" variant="outlined"
              sx={{ height: 16, fontSize: 9, '& .MuiChip-label': { px: 0.5 } }} />
          )}
          {subjectMatch && !isBusy && (
            <Chip label="Match" size="small" color="success" variant="outlined"
              sx={{ height: 16, fontSize: 9, '& .MuiChip-label': { px: 0.5 } }} />
          )}
        </Box>
      </Box>
    </Paper>
  );
}

// ─── AssignBottomSheet — mobile-optimised assign dialog ──────────────────────

interface AssignBottomSheetProps {
  /** If set, editing an existing assignment. If null, creating a new one. */
  assignment: DraftAssignment | null;
  /** Used when assignment is null (new assignment) */
  newCtx: NewAssignContext | null;
  absentTeacherIds: Set<string>;
  date: string;
  onClose: () => void;
  onAssign: (teacherId: string) => void;
  onRemove?: () => void;
  isPending: boolean;
  conflictInfo?: { type: string; label: string };
  cantAutoAssign?: boolean;
}

function AssignBottomSheet({
  assignment,
  newCtx,
  absentTeacherIds,
  date,
  onClose,
  onAssign,
  onRemove,
  isPending,
  conflictInfo,
  cantAutoAssign,
}: AssignBottomSheetProps) {
  const { schoolContext } = useAuthStore();

  const periodId = assignment?.period_id ?? newCtx?.periodId ?? '';
  const timeStart = assignment?.period.time_start ?? newCtx?.timeStart ?? '';
  const timeEnd = assignment?.period.time_end ?? newCtx?.timeEnd ?? '';
  const periodNumber = assignment?.period.period_number ?? newCtx?.periodNumber ?? 0;
  const className = assignment
    ? `${assignment.class_section.class.class_name}${assignment.class_section.section}`
    : newCtx?.className ?? '—';
  const subjectName = assignment?.subject.subject_name ?? newCtx?.subjectName ?? '—';
  const subjectId = (assignment as any)?.subject?.id ?? newCtx?.subjectId ?? '';
  const absentTeacherId = (assignment as any)?.absent_teacher_id ?? newCtx?.absentTeacherId ?? '';

  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(
    assignment?.substitute_teacher_id ?? null
  );

  const dayOfWeek = useMemo(() => {
    const days = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
    const [y, m, d] = date.split('-').map(Number);
    return days[new Date(y, m - 1, d).getDay()];
  }, [date]);

  const busyTeacherIds = useBusyTeacherIds(schoolContext, dayOfWeek, timeStart, timeEnd);

  const { data: teachers = [] } = useQuery({
    queryKey: queryKeys.teachers.list({ school_id: schoolContext || '' }),
    queryFn: () => getTeachersDirect({ school_id: schoolContext || undefined }),
    enabled: !!schoolContext,
  });

  const suggestions = useMemo(() => {
    return (teachers as any[])
      .filter((t) => t.is_active && !absentTeacherIds.has(t.id) && t.id !== absentTeacherId)
      .map((t) => ({
        teacher: t as Teacher,
        subjectMatch: !!(subjectId && Array.isArray(t.subjects) && t.subjects.some((s: any) => s.id === subjectId)),
        isBusy: busyTeacherIds.has(t.id),
      }))
      .sort((a, b) => {
        if (a.isBusy !== b.isBusy) return a.isBusy ? 1 : -1;
        if (a.subjectMatch !== b.subjectMatch) return a.subjectMatch ? -1 : 1;
        return a.teacher.name.localeCompare(b.teacher.name);
      })
      .slice(0, 5);
  }, [teachers, absentTeacherIds, absentTeacherId, subjectId, busyTeacherIds]);

  const selectedTeacherIsBusy = !!selectedTeacherId && busyTeacherIds.has(selectedTeacherId);
  const selectedTeacherName = useMemo(
    () => (teachers as any[]).find((t) => t.id === selectedTeacherId)?.name ?? 'This teacher',
    [teachers, selectedTeacherId]
  );

  const isEdit = !!assignment?.substitute_teacher_id;
  const hasConflict = !!conflictInfo;
  const modalTitle = hasConflict ? 'Fix Conflict' : isEdit ? 'Change Substitute' : 'Assign Substitute';

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        // On mobile: align dialog to bottom of screen like a bottom sheet
        '& .MuiDialog-container': {
          alignItems: { xs: 'flex-end', sm: 'center' },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: { xs: '16px 16px 0 0', sm: 2 },
          m: { xs: 0, sm: 2 },
          width: { xs: '100%', sm: undefined },
          maxWidth: { xs: '100%', sm: 600 },
          maxHeight: { xs: '90vh', sm: '80vh' },
        },
      }}
    >
      {/* Drag handle on mobile */}
      <Box sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'center', pt: 1.5, pb: 0.5 }}>
        <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'divider' }} />
      </Box>

      <DialogTitle sx={{ pb: 1, pt: { xs: 0.5, sm: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="medium" sx={{ fontSize: { xs: 16, sm: 20 } }}>
            {modalTitle}
          </Typography>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, overflowY: 'auto' }}>
        {/* Period + class info — single compact row */}
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'nowrap', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <SchoolIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12, fontWeight: 600 }}>
                P{periodNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                {timeStart.slice(0, 5)}–{timeEnd.slice(0, 5)}
              </Typography>
            </Box>
            <Box sx={{ width: 1, height: 14, bgcolor: 'divider', flexShrink: 0 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }} noWrap>
                {className}
              </Typography>
            </Box>
            <Box sx={{ width: 1, height: 14, bgcolor: 'divider', flexShrink: 0 }} />
            <Chip
              label={subjectName}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: 11, flexShrink: 0, '& .MuiChip-label': { px: 0.75 } }}
            />
          </Box>
        </Paper>

        {/* Conflict banner */}
        {conflictInfo && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#FEF2F2', borderColor: '#FECACA', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <WarningAmberIcon sx={{ fontSize: 16, color: '#991B1B', mt: 0.1, flexShrink: 0 }} />
              <Box>
                <Typography variant="body2" fontWeight="medium" sx={{ color: '#991B1B', fontSize: 13 }}>
                  {conflictInfo.label}
                </Typography>
                <Typography variant="caption" sx={{ color: '#B91C1C' }}>
                  Select a different teacher to resolve.
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Can't auto-assign banner */}
        {cantAutoAssign && !conflictInfo && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#FFFBEB', borderColor: '#FDE68A', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <WarningAmberIcon sx={{ fontSize: 16, color: '#92400E', mt: 0.1, flexShrink: 0 }} />
              <Box>
                <Typography variant="body2" fontWeight="medium" sx={{ color: '#92400E', fontSize: 13 }}>
                  No eligible teacher found automatically
                </Typography>
                <Typography variant="caption" sx={{ color: '#B45309' }}>
                  Assign manually below.
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Timetable conflict banner */}
        {selectedTeacherIsBusy && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#FEF2F2', borderColor: '#FECACA', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <WarningAmberIcon sx={{ fontSize: 16, color: '#991B1B', mt: 0.1, flexShrink: 0 }} />
              <Box>
                <Typography variant="body2" fontWeight="medium" sx={{ color: '#991B1B', fontSize: 13 }}>
                  {selectedTeacherName} is already teaching at this time
                </Typography>
                <Typography variant="caption" sx={{ color: '#B91C1C' }}>
                  Choose a different teacher.
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Quick suggestions */}
        {suggestions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="medium" sx={{ mb: 1, display: 'block' }}>
              Quick suggestions · Ranked by availability &amp; subject match
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                pb: 0.5,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {suggestions.map(({ teacher, subjectMatch, isBusy }) => (
                <SuggestionCard
                  key={teacher.id}
                  teacher={teacher}
                  subjectMatch={subjectMatch}
                  isBusy={isBusy}
                  isSelected={selectedTeacherId === teacher.id}
                  onClick={() => setSelectedTeacherId(teacher.id)}
                />
              ))}
            </Box>
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        <TeacherSelector
          value={selectedTeacherId}
          onChange={setSelectedTeacherId}
          targetDate={date}
          periodId={periodId}
          absentTeacherIds={[...absentTeacherIds]}
          busyTeacherIds={[...busyTeacherIds]}
          size="medium"
          label="Select substitute teacher"
          placeholder="Search by name…"
        />
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: { xs: 3, sm: 2.5 }, pt: 1, justifyContent: 'space-between' }}>
        {/* Remove — only for existing draft assignments */}
        {onRemove && assignment?.status === 'draft' ? (
          <Button
            variant="text"
            color="error"
            size="small"
            onClick={() => { onRemove(); onClose(); }}
            startIcon={<DeleteOutlineIcon />}
            sx={{ borderRadius: '4px !important', minHeight: 32 }}
          >
            Remove
          </Button>
        ) : (
          <Box />
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            sx={{ borderRadius: '4px !important', minHeight: 32 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={!selectedTeacherId || isPending || selectedTeacherIsBusy}
            onClick={() => { if (selectedTeacherId) onAssign(selectedTeacherId); }}
            startIcon={isPending ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{ borderRadius: '4px !important', minHeight: 32 }}
          >
            {hasConflict ? 'Reassign' : isEdit ? 'Change' : 'Assign'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// ─── Period pill color config ─────────────────────────────────────────────────

function getPillStyle(
  assignment: DraftAssignment | undefined,
  needsSubstitution: boolean,
  absentTeacherIds: Set<string>,
  schedulingConflict: boolean,
): {
  bg: string;
  border: string;
  dashed: boolean;
  statusDot: string;
  label: string;
} {
  if (!assignment && !needsSubstitution) {
    return { bg: 'transparent', border: '#E2E8F0', dashed: false, statusDot: '#CBD5E1', label: 'Free' };
  }
  if (!assignment && needsSubstitution) {
    return { bg: '#F8FAFC', border: '#94A3B8', dashed: true, statusDot: '#94A3B8', label: 'Needs sub' };
  }

  const isPosted = assignment!.status === 'posted';
  const isUnassigned = assignment!.substitute_teacher_id === null;
  const subIsAbsent = !!assignment!.substitute_teacher_id && absentTeacherIds.has(assignment!.substitute_teacher_id);
  const subIsInactive = assignment!.substitute_teacher != null && !assignment!.substitute_teacher.is_active;
  const hasConflict = subIsAbsent || subIsInactive || schedulingConflict;

  if (hasConflict)   return { bg: '#FEF2F2', border: '#DC2626', dashed: false, statusDot: '#DC2626', label: 'Conflict' };
  if (isPosted)      return { bg: '#F0FDF4', border: '#16A34A', dashed: false, statusDot: '#16A34A', label: 'Published' };
  if (!isUnassigned) return { bg: '#EFF6FF', border: '#2563EB', dashed: false, statusDot: '#2563EB', label: 'Draft' };
  return { bg: '#FFFBEB', border: '#D97706', dashed: true, statusDot: '#D97706', label: 'Unassigned' };
}

// ─── Period Pill ──────────────────────────────────────────────────────────────

interface PeriodPillProps {
  period: SchedulePeriod;
  assignment: DraftAssignment | undefined;
  needsSubstitution: boolean;
  timetableInfo?: { classSectionId: string; subjectId: string; className: string; section: string; subjectName: string };
  absentTeacherIds: Set<string>;
  schedulingConflict: boolean;
  isAdmin: boolean;
  onClick: () => void;
}

function PeriodPill({
  period,
  assignment,
  needsSubstitution,
  timetableInfo,
  absentTeacherIds,
  schedulingConflict,
  isAdmin,
  onClick,
}: PeriodPillProps) {
  const style = getPillStyle(assignment, needsSubstitution, absentTeacherIds, schedulingConflict);
  const isClickable = isAdmin && (needsSubstitution || (assignment && assignment.status !== 'posted'));
  const isFree = !assignment && !needsSubstitution;

  if (isFree) return null; // Don't render free periods on mobile — saves space

  const className = assignment
    ? `${assignment.class_section.class.class_name}${assignment.class_section.section}`
    : timetableInfo
    ? `${timetableInfo.className}${timetableInfo.section}`
    : '—';

  const subjectName = assignment?.subject.subject_name ?? timetableInfo?.subjectName ?? '—';
  const teacherName = assignment?.substitute_teacher?.name ?? null;
  const timeStr = `${period.time_start.slice(0, 5)}–${period.time_end.slice(0, 5)}`;

  return (
    <Box
      onClick={isClickable ? onClick : undefined}
      sx={{
        border: `2px ${style.dashed ? 'dashed' : 'solid'} ${style.border}`,
        borderRadius: 2,
        bgcolor: style.bg,
        p: 1.25,
        cursor: isClickable ? 'pointer' : 'default',
        minWidth: 120,
        flexShrink: 0,
        transition: 'all 0.15s',
        ...(isClickable && {
          '&:active': { opacity: 0.75, transform: 'scale(0.97)' },
        }),
      }}
    >
      {/* Period number + time */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: style.border }}>
          P{period.period_number}
        </Typography>
        <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{timeStr}</Typography>
      </Box>

      {/* Class + subject */}
      <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.2, color: 'text.primary' }} noWrap>
        {className}
      </Typography>
      <Typography sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1.2, mt: 0.25 }} noWrap>
        {subjectName}
      </Typography>

      {/* Substitute name or status */}
      <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: style.statusDot, flexShrink: 0 }} />
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 500,
            color: teacherName ? style.border : 'text.secondary',
            lineHeight: 1.2,
          }}
          noWrap
        >
          {teacherName ?? style.label}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Absence status chip config ───────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; bg: string; color: string; border: string }> = {
  pending:       { label: 'Needs Plan',  bg: '#FFFBEB', color: '#92400E', border: '#FDE68A' },
  draft_pending: { label: 'In Progress', bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
  active:        { label: 'In Progress', bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
  posted:        { label: 'Done',        bg: '#F0FDF4', color: '#166534', border: '#BBF7D0' },
  cancelled:     { label: 'Cancelled',   bg: '#F9FAFB', color: '#6B7280', border: '#E5E7EB' },
};

// ─── Main Card ────────────────────────────────────────────────────────────────

export function MobileSubstitutionCard({
  absence,
  date,
  absentTeacherIds,
  isAdmin,
  onStatsChange,
}: MobileSubstitutionCardProps) {
  const { schoolContext } = useAuthStore();
  const { success: showSuccess, error: showError } = useToast();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(true);
  const [autoAssigning, setAutoAssigning] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);
  const [progress, setProgress] = useState<AutoAssignProgress | null>(null);
  const [discarding, setDiscarding] = useState(false);
  const [recalling, setRecalling] = useState(false);

  // ── Assign modal state ─────────────────────────────────────────────────────
  /** Open with an existing assignment record */
  const [assignModal, setAssignModal] = useState<AssignModalState | null>(null);
  /** Open for a period with no DB record yet */
  const [newAssignCtx, setNewAssignCtx] = useState<NewAssignContext | null>(null);

  const assignModalOpen = assignModal !== null;
  const newAssignOpen = newAssignCtx !== null && assignModal === null;

  // ── Mutations ──────────────────────────────────────────────────────────────
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.absences.all });
  }, [queryClient]);

  const updateMutation = useMutation({
    mutationFn: ({ id, teacherId }: { id: string; teacherId: string }) =>
      updateAssignment(id, teacherId),
    onSuccess: () => { invalidate(); setAssignModal(null); showSuccess('Substitute assigned'); },
    onError: (err: any) => showError(err.message || 'Failed to assign'),
  });

  const createMutation = useMutation({
    mutationFn: (payload: Parameters<typeof createAssignment>[0]) => createAssignment(payload),
    onSuccess: () => { invalidate(); setNewAssignCtx(null); showSuccess('Substitute assigned'); },
    onError: (err: any) => showError(err.message || 'Failed to assign'),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => removeAssignment(id),
    onSuccess: () => { invalidate(); setAssignModal(null); showSuccess('Assignment removed'); },
    onError: (err: any) => showError(err.message || 'Failed to remove'),
  });

  const handleAssignExisting = useCallback((teacherId: string) => {
    if (!assignModal) return;
    updateMutation.mutate({ id: assignModal.assignment.id, teacherId });
  }, [assignModal, updateMutation]);

  const handleAssignNew = useCallback((teacherId: string) => {
    if (!newAssignCtx || !schoolContext) return;
    createMutation.mutate({
      absence_id: newAssignCtx.absenceId,
      period_id: newAssignCtx.periodId,
      date,
      substitute_teacher_id: teacherId,
      class_section_id: newAssignCtx.classSectionId,
      subject_id: newAssignCtx.subjectId,
      school_id: schoolContext,
      absent_teacher_id: newAssignCtx.absentTeacherId,
    });
  }, [newAssignCtx, date, schoolContext, createMutation]);

  // ── Fetch assignments ──────────────────────────────────────────────────────
  const { data: assignments = [] } = useQuery({
    queryKey: queryKeys.assignments.list({ absence_id: absence.id, date }),
    queryFn: () => getDraftAssignments(absence.id, date),
    enabled: !!absence.id,
  });

  // ── Fetch scheduled periods for this teacher ───────────────────────────────
  const dayOfWeek = useMemo(() => {
    const days = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
    const [y, m, d] = date.split('-').map(Number);
    return days[new Date(y, m - 1, d).getDay()];
  }, [date]);

  // Teacher's timetable records for this day (same pattern as SubstitutionGrid)
  const { data: teacherRecords = [] } = useQuery({
    queryKey: ['teacher-timetable', absence.teacher_id, dayOfWeek],
    queryFn: () => getByTeacherDirect(absence.teacher_id),
    enabled: !!absence.teacher_id,
    select: (records) => records.filter((r: any) => r.day_of_week === dayOfWeek),
  });

  // ── Fetch timetable records for conflict detection ─────────────────────────
  const { data: timetableRecords = [] } = useQuery({
    queryKey: [...queryKeys.timetable.all, 'records', schoolContext, dayOfWeek],
    queryFn: () =>
      getTimetableRecordsDirect({
        school_id: schoolContext!,
        day_of_week: dayOfWeek as any,
      }),
    enabled: !!schoolContext && !!dayOfWeek,
    staleTime: 30_000,
  });

  // ── Build period list from teacher's timetable records ─────────────────────
  const periods: SchedulePeriod[] = useMemo(() => {
    const seen = new Set<string>();
    const all: SchedulePeriod[] = [];
    for (const r of teacherRecords as any[]) {
      if (r.period && !seen.has(r.period.id)) {
        seen.add(r.period.id);
        all.push(r.period as SchedulePeriod);
      }
    }
    return all.sort((a, b) => a.period_number - b.period_number);
  }, [teacherRecords]);

  // ── Map period_id → timetable info ────────────────────────────────────────
  const timetableInfoMap = useMemo(() => {
    const map = new Map<string, { classSectionId: string; subjectId: string; className: string; section: string; subjectName: string }>();
    for (const r of teacherRecords as any[]) {
      if (!r.period_id) continue;
      map.set(r.period_id, {
        classSectionId: r.class_section_id ?? r.class_section?.id ?? '',
        subjectId: r.subject_id ?? r.subject?.id ?? '',
        className: r.class_section?.class?.class_name ?? '—',
        section: r.class_section?.section ?? '',
        subjectName: r.subject?.subject_name ?? '—',
      });
    }
    return map;
  }, [teacherRecords]);

  // ── Scheduling conflict detection ──────────────────────────────────────────
  const schedulingConflictIds = useMemo(() => {
    const conflictSet = new Set<string>();
    const subMap = new Map<string, { timeStart: string; timeEnd: string; assignmentId: string }[]>();
    for (const a of assignments) {
      if (!a.substitute_teacher_id || a.status !== 'draft') continue;
      const existing = subMap.get(a.substitute_teacher_id) ?? [];
      existing.push({ timeStart: a.period.time_start, timeEnd: a.period.time_end, assignmentId: a.id });
      subMap.set(a.substitute_teacher_id, existing);
    }
    for (const [, slots] of subMap) {
      for (let i = 0; i < slots.length; i++) {
        for (let j = i + 1; j < slots.length; j++) {
          if (slots[i].timeStart < slots[j].timeEnd && slots[i].timeEnd > slots[j].timeStart) {
            conflictSet.add(slots[i].assignmentId);
            conflictSet.add(slots[j].assignmentId);
          }
        }
      }
    }
    // Also check against timetable records
    for (const a of assignments) {
      if (!a.substitute_teacher_id || a.status !== 'draft') continue;
      for (const r of timetableRecords) {
        if (r.teacher_id !== a.substitute_teacher_id) continue;
        const rStart = r.period?.time_start;
        const rEnd = r.period?.time_end;
        if (!rStart || !rEnd) continue;
        if (rStart < a.period.time_end && rEnd > a.period.time_start) {
          conflictSet.add(a.id);
        }
      }
    }
    return conflictSet;
  }, [assignments, timetableRecords]);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    let pending = 0, covered = 0, published = 0;
    for (const a of assignments) {
      if (a.status === 'posted') published++;
      else if (a.substitute_teacher_id) covered++;
      else pending++;
    }
    // Also count unassigned periods (needsSubstitution but no assignment)
    for (const p of periods) {
      const hasAssignment = assignments.some((a) => a.period_id === p.id);
      if (!hasAssignment) pending++;
    }
    return { pending, covered, published };
  }, [assignments, periods]);

  useEffect(() => {
    onStatsChange?.(absence.id, stats.pending, stats.covered, stats.published);
  }, [absence.id, stats.pending, stats.covered, stats.published, onStatsChange]);

  const totalPeriods = periods.length;
  const coveredCount = stats.covered + stats.published;
  const coveragePercent = totalPeriods > 0 ? (coveredCount / totalPeriods) * 100 : 0;

  // ── Auto-assign ────────────────────────────────────────────────────────────
  const handleAutoAssign = useCallback(async () => {
    if (!schoolContext) return;
    setAutoAssigning(true);
    setProgress(null);
    setProgressOpen(true);
    try {
      const result = await triggerEngine(absence.id, date, (p) => setProgress(p));
      if (result.skipped_reason === 'draft_exists') {
        await rerunAutoAssignment(absence.id, date);
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.absences.all });
    } catch (err: any) {
      showError(err.message || 'Auto-assign failed');
    } finally {
      setAutoAssigning(false);
    }
  }, [absence.id, date, schoolContext, queryClient, showError]);

  // ── Discard draft ──────────────────────────────────────────────────────────
  const handleDiscard = useCallback(async () => {
    setDiscarding(true);
    try {
      await discardDraft(absence.id, date);
      queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.absences.all });
      showSuccess('Draft discarded');
    } catch (err: any) {
      showError(err.message || 'Discard failed');
    } finally {
      setDiscarding(false);
    }
  }, [absence.id, date, queryClient, showSuccess, showError]);

  // ── Recall & redraft ───────────────────────────────────────────────────────
  const handleRecall = useCallback(async () => {
    setRecalling(true);
    try {
      await recallAndRedraft(absence.id, date);
      queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.absences.all });
      showSuccess('Recalled to draft');
    } catch (err: any) {
      showError(err.message || 'Recall failed');
    } finally {
      setRecalling(false);
    }
  }, [absence.id, date, queryClient, showSuccess, showError]);

  const statusCfg = STATUS_CONFIG[absence.status] ?? STATUS_CONFIG.pending;
  const initials = absence.teacher.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const isPosted = absence.status === 'posted';
  const hasDrafts = assignments.some((a) => a.status === 'draft');
  const anyBusy = autoAssigning || discarding || recalling;

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          borderColor: 'divider',
        }}
      >
        {/* ── Header row ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.5,
            cursor: 'pointer',
            userSelect: 'none',
          }}
          onClick={() => setExpanded((e) => !e)}
        >
          {/* Avatar */}
          <Avatar
            sx={{
              width: 38,
              height: 38,
              fontSize: 14,
              fontWeight: 700,
              bgcolor: 'primary.light',
              color: 'primary.dark',
              flexShrink: 0,
            }}
          >
            {initials}
          </Avatar>

          {/* Name + meta */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3, color: 'text.primary' }}
              noWrap
            >
              {absence.teacher.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
              <Chip
                label={statusCfg.label}
                size="small"
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontWeight: 600,
                  bgcolor: statusCfg.bg,
                  color: statusCfg.color,
                  border: `1px solid ${statusCfg.border}`,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
              {totalPeriods > 0 && (
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                  {coveredCount}/{totalPeriods} covered
                </Typography>
              )}
            </Box>
          </Box>

          {/* Action icons */}
          {isAdmin && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
              {/* Auto-assign */}
              {!isPosted && (
                <Tooltip title="Auto-assign substitute">
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      disabled={anyBusy}
                      onClick={handleAutoAssign}
                      sx={{ width: 32, height: 32 }}
                    >
                      {autoAssigning ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <FlashOnIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {/* Discard draft */}
              {hasDrafts && !isPosted && (
                <Tooltip title="Discard draft">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={anyBusy}
                      onClick={handleDiscard}
                      sx={{ width: 32, height: 32 }}
                    >
                      {discarding ? (
                        <CircularProgress size={16} color="error" />
                      ) : (
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {/* Recall posted */}
              {isPosted && (
                <Tooltip title="Recall & redraft">
                  <span>
                    <IconButton
                      size="small"
                      disabled={anyBusy}
                      onClick={handleRecall}
                      sx={{ width: 32, height: 32 }}
                    >
                      {recalling ? (
                        <CircularProgress size={16} />
                      ) : (
                        <UndoIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          )}

          {/* Expand toggle */}
          <IconButton size="small" sx={{ width: 28, height: 28, color: 'text.secondary' }}>
            {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Box>

        {/* ── Coverage progress bar ── */}
        {totalPeriods > 0 && (
          <Box sx={{ px: 2, pb: expanded ? 0 : 1.5 }}>
            <LinearProgress
              variant="determinate"
              value={coveragePercent}
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 2,
                  bgcolor:
                    stats.published > 0 && stats.pending === 0 && stats.covered === 0
                      ? '#16A34A'
                      : stats.covered > 0
                      ? '#2563EB'
                      : '#D97706',
                },
              }}
            />
          </Box>
        )}

        {/* ── Period pills (collapsible) ── */}
        <Collapse in={expanded}>
          <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
            {periods.length === 0 ? (
              <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>
                No scheduled periods found for this day.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  overflowX: 'auto',
                  pb: 0.5,
                  // Hide scrollbar on mobile but keep scroll
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}
              >
                {periods.map((period) => {
                  const assignment = assignments.find((a) => a.period_id === period.id);
                  const timetableInfo = timetableInfoMap.get(period.id);
                  const needsSubstitution = !!timetableInfo;
                  const schedulingConflict = assignment ? schedulingConflictIds.has(assignment.id) : false;

                  return (
                    <PeriodPill
                      key={period.id}
                      period={period}
                      assignment={assignment}
                      needsSubstitution={needsSubstitution}
                      timetableInfo={timetableInfo}
                      absentTeacherIds={absentTeacherIds}
                      schedulingConflict={schedulingConflict}
                      isAdmin={isAdmin}
                      onClick={() => {
                        if (!isAdmin) return;
                        if (assignment) {
                          // Existing assignment — open edit modal
                          const subIsAbsent = !!assignment.substitute_teacher_id && absentTeacherIds.has(assignment.substitute_teacher_id);
                          const subIsInactive = assignment.substitute_teacher != null && !assignment.substitute_teacher.is_active;
                          const isSchedulingConflict = schedulingConflictIds.has(assignment.id);
                          let conflictInfo: AssignModalState['conflictInfo'];
                          if (subIsAbsent) conflictInfo = { type: 'absent', label: `${assignment.substitute_teacher?.name ?? 'Substitute'} is absent on this date` };
                          else if (subIsInactive) conflictInfo = { type: 'inactive', label: `${assignment.substitute_teacher?.name ?? 'Substitute'} is no longer active` };
                          else if (isSchedulingConflict) conflictInfo = { type: 'scheduling', label: 'This teacher is already assigned to another class at this time' };
                          setAssignModal({
                            assignment,
                            conflictInfo,
                            cantAutoAssign: assignment.unassigned_reason === 'no_eligible_teachers',
                          });
                        } else if (timetableInfo) {
                          // No assignment yet — open new assign modal
                          setNewAssignCtx({
                            absenceId: absence.id,
                            absentTeacherId: absence.teacher_id,
                            periodId: period.id,
                            classSectionId: timetableInfo.classSectionId,
                            subjectId: timetableInfo.subjectId,
                            className: `${timetableInfo.className}${timetableInfo.section}`,
                            subjectName: timetableInfo.subjectName,
                            periodNumber: period.period_number,
                            timeStart: period.time_start,
                            timeEnd: period.time_end,
                          });
                        }
                      }}
                    />
                  );
                })}
              </Box>
            )}

            {/* Conflict warning */}
            {schedulingConflictIds.size > 0 && (
              <Box
                sx={{
                  mt: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.25,
                  py: 0.75,
                  bgcolor: '#FEF2F2',
                  borderRadius: 1.5,
                  border: '1px solid #FECACA',
                }}
              >
                <WarningAmberIcon sx={{ fontSize: 14, color: '#DC2626', flexShrink: 0 }} />
                <Typography sx={{ fontSize: 11, color: '#991B1B', fontWeight: 500 }}>
                  Scheduling conflict — tap the red period to fix
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Auto-assign progress dialog */}
      <AutoAssignProgressDialog
        open={progressOpen}
        progress={progress}
        isRunning={autoAssigning}
        onClose={() => {
          setProgressOpen(false);
          setProgress(null);
        }}
      />

      {/* Assign modal — editing existing assignment */}
      {assignModalOpen && assignModal && (
        <AssignBottomSheet
          assignment={assignModal.assignment}
          newCtx={null}
          absentTeacherIds={absentTeacherIds}
          date={date}
          onClose={() => setAssignModal(null)}
          onAssign={handleAssignExisting}
          onRemove={() => removeMutation.mutate(assignModal.assignment.id)}
          isPending={updateMutation.isPending || removeMutation.isPending}
          conflictInfo={assignModal.conflictInfo}
          cantAutoAssign={assignModal.cantAutoAssign}
        />
      )}

      {/* Assign modal — new assignment (no DB record yet) */}
      {newAssignOpen && newAssignCtx && (
        <AssignBottomSheet
          assignment={null}
          newCtx={newAssignCtx}
          absentTeacherIds={absentTeacherIds}
          date={date}
          onClose={() => setNewAssignCtx(null)}
          onAssign={handleAssignNew}
          isPending={createMutation.isPending}
        />
      )}
    </>
  );
}
